// On-site meeting recorder (design decisions #3, #8). MediaRecorder → ~30s chunks
// → IndexedDB buffer (crash/offline-safe) → background retrying uploader → S3 via
// the API. Survives tab crash, laptop sleep (wake-lock), and dead hangar wifi:
// chunks just accumulate in IndexedDB and upload when connectivity returns.
//
// Pure helpers + a useMeetingRecorder hook. No third-party deps — MediaRecorder,
// IndexedDB, and the Wake Lock API are all native.

import { useCallback, useEffect, useRef, useState } from "react";
import { startMeeting, uploadMeetingChunk, finalizeMeeting } from "../../lib/brain-api";

const CHUNK_MS = 30_000;
const DB_NAME = "aeronet-meetings";
const STORE = "chunks";

interface ChunkRec {
  meetingId: string;
  seq: number;
  customerId: string;
  visitId: string;
  contentType: string;
  blob: Blob;
}

// --- IndexedDB (compound key [meetingId, seq]) -----------------------------

let dbPromise: Promise<IDBDatabase> | null = null;
function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const os = db.createObjectStore(STORE, { keyPath: ["meetingId", "seq"] });
        os.createIndex("byMeeting", "meetingId", { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function tx<T>(mode: IDBTransactionMode, fn: (os: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const req = fn(t.objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      })
  );
}

async function idbPutChunk(rec: ChunkRec): Promise<void> {
  await tx("readwrite", (os) => os.put(rec));
}
async function idbDeleteChunk(meetingId: string, seq: number): Promise<void> {
  await tx("readwrite", (os) => os.delete([meetingId, seq]));
}
async function idbChunksFor(meetingId: string): Promise<ChunkRec[]> {
  const all = await tx<ChunkRec[]>("readonly", (os) => os.index("byMeeting").getAll(meetingId));
  return all.sort((a, b) => a.seq - b.seq);
}
/** Distinct meetingIds that still have buffered (un-uploaded) chunks — resume set. */
export async function unfinishedMeetings(): Promise<{ meetingId: string; customerId: string; visitId: string; chunks: number }[]> {
  const all = await tx<ChunkRec[]>("readonly", (os) => os.getAll());
  const by = new Map<string, { meetingId: string; customerId: string; visitId: string; chunks: number }>();
  for (const c of all) {
    const cur = by.get(c.meetingId);
    if (cur) cur.chunks++;
    else by.set(c.meetingId, { meetingId: c.meetingId, customerId: c.customerId, visitId: c.visitId, chunks: 1 });
  }
  return [...by.values()];
}
export async function discardBuffered(meetingId: string): Promise<void> {
  for (const c of await idbChunksFor(meetingId)) await idbDeleteChunk(meetingId, c.seq);
}

// --- audio format + base64 -------------------------------------------------

/**
 * Pick a MediaRecorder container the browser supports AND Gemini can read.
 * Order prefers Gemini-documented formats (mp4/aac, ogg/opus) before webm/opus.
 */
export function pickAudioMime(): { recorderMime: string; storeMime: string } {
  const candidates = [
    { recorderMime: "audio/mp4", storeMime: "audio/mp4" },
    { recorderMime: "audio/ogg;codecs=opus", storeMime: "audio/ogg" },
    { recorderMime: "audio/webm;codecs=opus", storeMime: "audio/webm" },
    { recorderMime: "audio/webm", storeMime: "audio/webm" },
  ];
  const canProbe = typeof MediaRecorder !== "undefined" && typeof MediaRecorder.isTypeSupported === "function";
  for (const c of candidates) {
    if (!canProbe || MediaRecorder.isTypeSupported(c.recorderMime)) return c;
  }
  return { recorderMime: "", storeMime: "audio/webm" }; // let the browser default
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buf = new Uint8Array(await blob.arrayBuffer());
  let bin = "";
  const step = 0x8000;
  for (let i = 0; i < buf.length; i += step) bin += String.fromCharCode(...buf.subarray(i, i + step));
  return btoa(bin);
}

// --- the hook --------------------------------------------------------------

export type RecorderStatus = "idle" | "recording" | "paused" | "finalizing" | "error";

export interface UseMeetingRecorder {
  status: RecorderStatus;
  meetingId: string | null;
  elapsedSec: number;
  uploaded: number;
  buffered: number;
  error: string | null;
  start: (opts: { customerId: string; visitId: string; title?: string; attendees?: string[] }) => Promise<void>;
  pause: () => void;
  resume: () => void;
  stop: (meta?: { title?: string; attendees?: string[] }) => Promise<string | null>;
  /** Finish uploading + finalize a meeting whose chunks survived a crash. */
  resumeBuffered: (meetingId: string, customerId: string, visitId: string) => Promise<void>;
}

export function useMeetingRecorder(onDone?: () => void): UseMeetingRecorder {
  const [status, setStatus] = useState<RecorderStatus>("idle");
  const [meetingId, setMeetingId] = useState<string | null>(null);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [uploaded, setUploaded] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const wakeRef = useRef<{ release: () => Promise<void> } | null>(null);
  const seqRef = useRef(0);
  const meetingRef = useRef<{ id: string; customerId: string; visitId: string; storeMime: string } | null>(null);
  const drainingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refreshBuffered = useCallback(async (mId: string) => {
    setBuffered((await idbChunksFor(mId)).length);
  }, []);

  // Upload every buffered chunk for the active meeting, deleting each on success.
  // Guarded so only one drain runs at a time; failures leave chunks for a retry.
  const drain = useCallback(async () => {
    const m = meetingRef.current;
    if (!m || drainingRef.current) return;
    drainingRef.current = true;
    try {
      const chunks = await idbChunksFor(m.id);
      for (const c of chunks) {
        try {
          const contentBase64 = await blobToBase64(c.blob);
          await uploadMeetingChunk({
            customerId: c.customerId,
            visitId: c.visitId,
            meetingId: c.meetingId,
            seq: c.seq,
            contentBase64,
            contentType: c.contentType,
          });
          await idbDeleteChunk(c.meetingId, c.seq);
          setUploaded((n) => n + 1);
        } catch {
          break; // offline / server error — stop; a later tick retries
        }
      }
      await refreshBuffered(m.id);
    } finally {
      drainingRef.current = false;
    }
  }, [refreshBuffered]);

  const releaseWake = useCallback(async () => {
    try {
      await wakeRef.current?.release();
    } catch {
      /* ignore */
    }
    wakeRef.current = null;
  }, []);

  const acquireWake = useCallback(async () => {
    try {
      const wl = (navigator as unknown as { wakeLock?: { request: (t: string) => Promise<{ release: () => Promise<void> }> } }).wakeLock;
      if (wl) wakeRef.current = await wl.request("screen");
    } catch {
      /* wake lock is best-effort */
    }
  }, []);

  const start = useCallback(
    async (opts: { customerId: string; visitId: string; title?: string; attendees?: string[] }) => {
      setError(null);
      try {
        const { recorderMime, storeMime } = pickAudioMime();
        const { meetingId: id } = await startMeeting({
          customerId: opts.customerId,
          visitId: opts.visitId,
          title: opts.title,
          attendees: opts.attendees,
          audioMimeType: storeMime,
        });
        meetingRef.current = { id, customerId: opts.customerId, visitId: opts.visitId, storeMime };
        seqRef.current = 0;
        setMeetingId(id);
        setUploaded(0);
        setBuffered(0);
        setElapsedSec(0);

        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;
        const rec = recorderMime ? new MediaRecorder(stream, { mimeType: recorderMime }) : new MediaRecorder(stream);
        recorderRef.current = rec;
        rec.ondataavailable = (e) => {
          if (!e.data || e.data.size === 0 || !meetingRef.current) return;
          const seq = seqRef.current++;
          void idbPutChunk({
            meetingId: meetingRef.current.id,
            seq,
            customerId: meetingRef.current.customerId,
            visitId: meetingRef.current.visitId,
            contentType: meetingRef.current.storeMime,
            blob: e.data,
          }).then(() => {
            void refreshBuffered(meetingRef.current!.id);
            void drain();
          });
        };
        rec.start(CHUNK_MS);
        await acquireWake();
        setStatus("recording");
        timerRef.current = setInterval(() => setElapsedSec((s) => s + 1), 1000);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Could not start recording");
        setStatus("error");
      }
    },
    [acquireWake, drain, refreshBuffered]
  );

  const pause = useCallback(() => {
    if (recorderRef.current?.state === "recording") {
      recorderRef.current.requestData();
      recorderRef.current.pause();
      setStatus("paused");
    }
  }, []);

  const resume = useCallback(() => {
    if (recorderRef.current?.state === "paused") {
      recorderRef.current.resume();
      setStatus("recording");
    }
  }, []);

  const stop = useCallback(async (meta?: { title?: string; attendees?: string[] }): Promise<string | null> => {
    const m = meetingRef.current;
    const rec = recorderRef.current;
    if (!m || !rec) return null;
    setStatus("finalizing");
    if (timerRef.current) clearInterval(timerRef.current);

    // Flush the final chunk, then stop the tracks.
    await new Promise<void>((resolve) => {
      rec.onstop = () => resolve();
      if (rec.state !== "inactive") rec.stop();
      else resolve();
    });
    streamRef.current?.getTracks().forEach((t) => t.stop());
    await releaseWake();

    // Drain remaining chunks (bounded retries so we don't hang forever offline).
    for (let i = 0; i < 20; i++) {
      await drain();
      if ((await idbChunksFor(m.id)).length === 0) break;
      await new Promise((r) => setTimeout(r, 1500));
    }
    const remaining = (await idbChunksFor(m.id)).length;
    if (remaining > 0) {
      setError(`${remaining} chunk(s) could not be uploaded — reopen this tab on a connection to finish.`);
      setStatus("error");
      return null;
    }

    try {
      await finalizeMeeting({
        customerId: m.customerId,
        visitId: m.visitId,
        meetingId: m.id,
        chunkCount: seqRef.current,
        durationSec: elapsedSec,
        audioMimeType: m.storeMime,
        title: meta?.title,
        attendees: meta?.attendees,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not finalize meeting");
      setStatus("error");
      return null;
    }
    setStatus("idle");
    meetingRef.current = null;
    recorderRef.current = null;
    onDone?.();
    return m.id;
  }, [drain, elapsedSec, onDone, releaseWake]);

  const resumeBuffered = useCallback(
    async (mId: string, customerId: string, visitId: string) => {
      const chunks = await idbChunksFor(mId);
      meetingRef.current = { id: mId, customerId, visitId, storeMime: chunks[0]?.contentType ?? "audio/webm" };
      seqRef.current = chunks.length ? chunks[chunks.length - 1].seq + 1 : 0;
      setStatus("finalizing");
      for (let i = 0; i < 20; i++) {
        await drain();
        if ((await idbChunksFor(mId)).length === 0) break;
        await new Promise((r) => setTimeout(r, 1500));
      }
      if ((await idbChunksFor(mId)).length === 0) {
        await finalizeMeeting({ customerId, visitId, meetingId: mId, chunkCount: seqRef.current });
        onDone?.();
      }
      meetingRef.current = null;
      setStatus("idle");
    },
    [drain, onDone]
  );

  // Warn before closing the tab mid-recording (unsaved audio).
  useEffect(() => {
    if (status !== "recording" && status !== "paused") return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [status]);

  // Re-acquire the wake lock if the tab was backgrounded and returns.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible" && status === "recording" && !wakeRef.current) void acquireWake();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [acquireWake, status]);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  return { status, meetingId, elapsedSec, uploaded, buffered, error, start, pause, resume, stop, resumeBuffered };
}
